import {
  getClipPlaybackRate,
  VIDEO_EDITOR_MIN_PLAYBACK_RATE,
  VIDEO_EDITOR_TIMELINE_EFFECT,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { getClipSourceDuration, isSourceBoundedClip } from "./clipTimelineUtils";
import { getTimelineRowsLayout } from "./timelineLayout";
import { getClipTimelineDuration, getTrackByType } from "./projectSchema";
import { clampSubtitleClipNeighbors } from "./subtitleEdl";

const EFFECT_ID_BY_CLIP_TYPE = {
  video: VIDEO_EDITOR_TIMELINE_EFFECT.VIDEO,
  image: VIDEO_EDITOR_TIMELINE_EFFECT.IMAGE,
  audio: VIDEO_EDITOR_TIMELINE_EFFECT.AUDIO,
  subtitle: VIDEO_EDITOR_TIMELINE_EFFECT.SUBTITLE,
};

export const buildTimelineEffects = () => ({
  [VIDEO_EDITOR_TIMELINE_EFFECT.VIDEO]: {
    id: VIDEO_EDITOR_TIMELINE_EFFECT.VIDEO,
    name: "Video",
  },
  [VIDEO_EDITOR_TIMELINE_EFFECT.IMAGE]: {
    id: VIDEO_EDITOR_TIMELINE_EFFECT.IMAGE,
    name: "Image",
  },
  [VIDEO_EDITOR_TIMELINE_EFFECT.AUDIO]: {
    id: VIDEO_EDITOR_TIMELINE_EFFECT.AUDIO,
    name: "Audio",
  },
  [VIDEO_EDITOR_TIMELINE_EFFECT.SUBTITLE]: {
    id: VIDEO_EDITOR_TIMELINE_EFFECT.SUBTITLE,
    name: "Subtitle",
  },
});

const buildRowActions = ({
  edl,
  track,
  selectedClipIds = [],
  draggingClipIds = [],
}) => {
  if (!track) return [];

  return (track.clipIds || [])
    .map((clipId) => edl.clips.byId[clipId])
    .filter(Boolean)
    .map((clip) => {
      const inPoint = Number(clip.inPoint) || 0;
      const sourceDuration = getClipSourceDuration(clip);
      const timelineDuration = getClipTimelineDuration({ clip });
      const start = clip.position;
      const end = start + timelineDuration;
      const maxEnd =
        isSourceBoundedClip(clip) && sourceDuration
          ? start +
            (sourceDuration - inPoint) / VIDEO_EDITOR_MIN_PLAYBACK_RATE
          : undefined;

      return {
        id: clip.id,
        start,
        end,
        effectId:
          EFFECT_ID_BY_CLIP_TYPE[clip.type] ||
          VIDEO_EDITOR_TIMELINE_EFFECT.VIDEO,
        flexible: false,
        movable: false,
        selected: selectedClipIds.includes(clip.id),
        maxEnd,
        minStart: 0,
        classNames: [
          draggingClipIds.includes(clip.id) ? "video-editor-clip-source-hidden" : "",
        ].filter(Boolean),
        data: {
          clipId: clip.id,
          trackId: track.id,
          type: clip.type,
          placement: clip.placement,
          label: clip.label,
          text: clip.text,
          sourceUrl: clip.sourceUrl,
          inPoint: clip.inPoint,
          outPoint: clip.outPoint,
          layerIndex: clip.layerIndex || 0,
          playbackRate: getClipPlaybackRate({ clip }),
          volume: clip.volume,
        },
      };
    });
};

export const edlToTimelineRows = ({
  edl,
  selectedClipId = null,
  selectedClipIds = null,
  draggingClipId = null,
  draggingClipIds = null,
}) => {
  if (!edl) return [];

  const { rows } = getTimelineRowsLayout({ edl });
  const resolvedSelectedIds =
    selectedClipIds || (selectedClipId ? [selectedClipId] : []);
  const resolvedDraggingIds =
    draggingClipIds || (draggingClipId ? [draggingClipId] : []);

  return rows.map((rowMeta) => {
    const track = rowMeta.trackId
      ? edl.tracks.byId[rowMeta.trackId]
      : getTrackByType({ edl, type: rowMeta.type });

    return {
      id: track?.id || rowMeta.id,
      type: rowMeta.type,
      trackId: track?.id,
      rowHeight: rowMeta.slotHeight,
      classNames: [
        rowMeta.isContentRow
          ? "video-editor-timeline-row-content"
          : "video-editor-timeline-row-compact",
        `video-editor-timeline-row-${rowMeta.type}`,
      ],
      actions: buildRowActions({
        edl,
        track,
        selectedClipIds: resolvedSelectedIds,
        draggingClipIds: resolvedDraggingIds,
      }),
    };
  });
};

export const timelineActionToClipUpdates = ({ action, edl }) => {
  const clip = edl?.clips?.byId?.[action?.id];
  if (!clip) return null;

  const rawStart = action.start;
  const rawEnd = action.end;
  const { start, end } =
    clip.type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE
      ? clampSubtitleClipNeighbors({
          edl,
          clipId: clip.id,
          start: rawStart,
          end: rawEnd,
        })
      : { start: rawStart, end: rawEnd };

  const duration = Math.max((end || 0) - (start || 0), 0.1);
  const inPoint = Number(clip.inPoint) || 0;
  const sourceDuration = getClipSourceDuration(clip);
  const playbackRate = getClipPlaybackRate({ clip });
  const sourceWindow = Math.max(
    (Number(clip.outPoint) || 0) - inPoint,
    0.1
  );

  if (isSourceBoundedClip(clip) && duration > sourceWindow) {
    const availableSource = Math.max(sourceDuration - inPoint, 0.1);
    return {
      clipId: clip.id,
      updates: {
        position: start,
        inPoint,
        outPoint: inPoint + availableSource,
        playbackRate: Math.min(
          1,
          Math.max(VIDEO_EDITOR_MIN_PLAYBACK_RATE, availableSource / duration)
        ),
      },
    };
  }

  return {
    clipId: clip.id,
    updates: {
      position: start,
      inPoint,
      outPoint: inPoint + duration,
      playbackRate,
    },
  };
};

export const findClipAtPlayhead = ({ edl, playhead }) => {
  const clips = Object.values(edl?.clips?.byId || {});
  return clips.find((clip) => {
    const start = clip.position;
    const end = start + getClipTimelineDuration({ clip });
    return playhead > start && playhead < end;
  });
};

export const getTrackTypeFromRow = ({ row }) =>
  row?.type || VIDEO_EDITOR_TRACK_TYPE.VIDEO;

export const getRowTrackId = ({ row }) => row?.trackId || row?.id;
